import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import _get from 'lodash.get';

import {
    Question,
    Select,
    CalculatorStepAccordion,
    FormComputed,
    MessagePropType,
    useForm,
    useCalculator,
} from '@ratehub/base-ui';
import { cmhc_pst } from '@ratehub/mortgage-calculations';

import { getCashNeeded } from '../functions/getCashNeededCalculations';
import getPaymentCalculatorCMHCInsurance from '../functions/getPaymentCalculatorCMHCInsurance';
import getMortgageScenarioChooserOptionsPayment from '../functions/getMortgageScenarioChooserOptionsPayment';
import getSelectedPaymentCalculatorScenario from '../functions/getSelectedPaymentCalculatorScenario';
import HOME_TYPE_OPTIONS, { HOME_TYPES } from '../definitions/HomeTypes';
import CASH_NEEDED_DEFAULT_VALUES, { CASH_NEEDED } from '../definitions/CashNeededDefaultValues';
import PaymentCalculatorScenarioCalculations from './PaymentCalculatorScenarioCalculations';


function PaymentCalculatorStepCashNeeded({
    stepNumber,
    scenarioCount,
    introduction,

    onToggleExpand,
    onChangeScenario,
    ...otherProps
}) {
    const intl = useIntl();
    const form = useForm();
    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const { scenarioType, selectedScenarioNumber, homeType } = form.values;

    const cashNeededValues = getSavedCashNeededValues(toJS(document?.journey?.calculators?.payment));

    // NOTE: length of cashNeededValues will always be constant
    // therefore its safe to add the values to dependency array
    const lines = useMemo(
        () => getLines(selectedScenarioNumber - 1, homeType, cashNeededValues),
        [ selectedScenarioNumber, homeType, ...Object.values(cashNeededValues) ],
    );

    return (
        <>
            <FormComputed
                name="cashNeeded"
                value={(values) => getCashNeeded({
                    downPayment: _get(values, `scenarios[${values.selectedScenarioNumber - 1}].downPayment`),
                    PSTCalculation: values.pstCalculation,
                    lawyerFeeCalculation: values.lawyerFee,
                    homeInspectionCalculation: values.homeInspection,
                    appraisalCalculation: values.appraisalFee,
                    landTransferCalculation: values.landTransferTax,
                    titleInsuranceCalculation: values.titleInsurance,
                    estoppelFeeCalculation: values.estoppelFee,
                })}
            />

            <CalculatorStepAccordion
                accordionDataName={`calculator-step[${stepNumber}].mtg-paymentCalculator-cashNeededAccordion`}
                title={(
                    <FormattedMessage
                        id="mtg-calculators.PaymentCalculatorCashNeeded.title"
                        defaultMessage="Cash needed to close"
                    />
                )}
                stepNumber={stepNumber}
                showBadge={false}
                value="cashNeeded"
                valueFormat="CAD0"
                onToggleExpand={onToggleExpand}
                canBeDisabled
                {...otherProps}
            >
                <PaymentCalculatorScenarioCalculations
                    lines={lines}
                    result={{
                        name: 'cashNeededResult',
                        label: (
                            <FormattedMessage
                                id="calculators.PaymentCalculatorCashNeeded.resultLabel"
                                defaultMessage="Cash needed to close"
                            />
                        ),
                        value: 'cashNeeded',
                    }}
                >
                    <p className="rh-mt-0">
                        {introduction ?? intl.formatMessage(MESSAGES.cashIntroduction)}
                    </p>

                    <If condition={scenarioCount > 1}>
                        <label
                            className="rh-title-xs rh-mb-1 rh-display-block weight-medium"
                            htmlFor="scenarioOptions"
                        >
                            <FormattedMessage
                                id="calculators.PaymentCalculatorCashNeeded.downPaymentOptionsLabel"
                                defaultMessage="Down payment options"
                            />
                        </label>

                        <Select
                            id="scenarioOptions"
                            name="scenarioOptions"
                            options={getMortgageScenarioChooserOptionsPayment(scenarioType, scenarioCount)}
                            value={getSelectedPaymentCalculatorScenario(selectedScenarioNumber, scenarioCount)}
                            onChange={onChangeScenario}
                            type="dropdown"
                        />
                    </If>

                    <Question
                        className="rh-mb-0"
                        field={{
                            name: 'homeType',
                            type: 'choice',
                            options: HOME_TYPE_OPTIONS,
                            value: cashNeededValues?.[CASH_NEEDED.ESTOPPEL_FEE] ? HOME_TYPES.CONDO : HOME_TYPES.HOUSE,
                        }}
                        labelMessage={(
                            <FormattedMessage
                                id="calculators.PaymentCalculatorCashNeeded.homeTypeLabel"
                                defaultMessage="Type of house"
                            />
                        )}
                        flowDirection="row"
                    />
                </PaymentCalculatorScenarioCalculations>
            </CalculatorStepAccordion>
        </>
    );
}

PaymentCalculatorStepCashNeeded.propTypes = {
    stepNumber: PropTypes.number.isRequired,
    scenarioCount: PropTypes.number.isRequired,
    introduction: MessagePropType,

    onChangeScenario: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func,
};

PaymentCalculatorStepCashNeeded.defaultProps = {
    introduction: undefined,

    onToggleExpand: undefined,
};

function getSavedCashNeededValues(savedCashNeeded) {
    if (!savedCashNeeded) {
        return CASH_NEEDED_DEFAULT_VALUES;
    }

    return {
        [CASH_NEEDED.LAWYER_FEE]: savedCashNeeded?.lawyerFees,
        [CASH_NEEDED.TITLE_INSURANCE]: savedCashNeeded?.titleInsuranceCost,
        [CASH_NEEDED.HOME_INSPECTION]: savedCashNeeded?.homeInspectionCost,
        [CASH_NEEDED.APPRAISAL_FEE]: savedCashNeeded?.appraisalFees,
        [CASH_NEEDED.ESTOPPEL_FEE]: savedCashNeeded?.estoppelFees,
    };
}

function getPSTCalculation({ homeLocation, cmhcCalculation }) {
    if (homeLocation?.province?.code && cmhcCalculation) {
        return cmhc_pst(cmhcCalculation, homeLocation.province.code);
    }

    return null;
}

function getLines(selectedScenario, homeType, values) {
    const houseLines = [
        {
            id: 'downPayment',
            value: `scenarios[${selectedScenario}].downPayment.dollars`,
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.downPaymentCalculationLabel"
                    defaultMessage="Down payment"
                />
            ),
        },
        {
            id: 'landTransferTax',
            value: 'landTransferTax',
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.landTransferTaxCalculationLabel"
                    defaultMessage="Land transfer tax"
                />
            ),
        },
        {
            id: 'pstCalculation',
            name: 'pstCalculation',
            value: (formValues) => getPSTCalculation({
                homeLocation: formValues.location,
                cmhcCalculation: getPaymentCalculatorCMHCInsurance({
                    formValues,
                    paymentScenario: selectedScenario,
                }),
            }),
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.pstCalculationLabel"
                    defaultMessage="PST on mortgage insurance"
                />
            ),
        },
        {
            id: CASH_NEEDED.LAWYER_FEE,
            name: 'lawyerFee',
            value: {
                name: 'lawyerFee',
                type: 'integer',
                value: values[CASH_NEEDED.LAWYER_FEE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.lawyerFeeLabel"
                    defaultMessage="Lawyer fees"
                />
            ),
        },
        {
            id: CASH_NEEDED.TITLE_INSURANCE,
            value: {
                name: 'titleInsurance',
                type: 'integer',
                value: values[CASH_NEEDED.TITLE_INSURANCE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.titleInsuranceLabel"
                    defaultMessage="Title insurance"
                />
            ),
        },
        {
            id: CASH_NEEDED.HOME_INSPECTION,
            value: {
                name: 'homeInspection',
                type: 'integer',
                value: values[CASH_NEEDED.HOME_INSPECTION],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.homeInspectionLabel"
                    defaultMessage="Home inspection"
                />
            ),
        },
        {
            id: CASH_NEEDED.APPRAISAL_FEE,
            value: {
                name: 'appraisalFee',
                type: 'integer',
                value: values[CASH_NEEDED.APPRAISAL_FEE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.appraisalFeeLabel"
                    defaultMessage="Appraisal fees"
                />
            ),
        },
    ];

    const condoLines = [
        {
            id: CASH_NEEDED.ESTOPPEL_FEE,
            value: {
                name: 'estoppelFee',
                type: 'integer',
                value: values[CASH_NEEDED.ESTOPPEL_FEE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepCashNeeded.estoppelFeeLabel"
                    defaultMessage="Estoppel fee"
                />
            ),
        },
    ];

    return homeType === HOME_TYPES.HOUSE
        ? [ ...houseLines ]
        : [ ...houseLines, ...condoLines ];

}

const MESSAGES = defineMessages({
    cashIntroduction: {
        id: 'mtg-calculators.PaymentCalculatorCashNeeded.cashNeededInformation',
        defaultMessage: 'When you purchase a house, there are a number of costs you will need to put aside in addition to your down payment.',
    },
});


export default observer(PaymentCalculatorStepCashNeeded);
